import { useAppOrgsAndInvitedOrgs } from "@/components/AppProvider/AppOrgsAndInvitedOrgsProvider";
import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";
import React from "react";
import { useAppRouteParams } from "../../../../../AppRoutes";
import { postCancelVersion } from "../../../../../api/version";
import { useAppNotificationManager } from "../../../../../components/AppProvider/AppNotificationProvider";
import { Version } from "../../../../../domain/Version";
import { DialogPhaseStatesType, useDialog } from "../../../helpers/hooks";

const useTranslation = translation.use(nsVersion);

export const DialogVersionCancel: React.FC<
  {
    versions: Version[];
    selectedVersionIds: string[];
    setSelectedVersionIds: React.Dispatch<React.SetStateAction<string[]>>;
  } & DialogPhaseStatesType
> = ({
  versions,
  selectedVersionIds,
  setSelectedVersionIds,
  dialogPhase,
  setDialogPhase,
}) => {
  const { t } = useTranslation();
  const { organizationId } = useAppRouteParams();
  const { orgServiceType } = useAppOrgsAndInvitedOrgs();
  const target =
    orgServiceType === "planium"
      ? t.version.cancel.target.planium()
      : t.version.cancel.target.optium();

  const { renderDialogs, showErrorAPIDialog } = useDialog({
    dialogPhase,
    setDialogPhase,
  });

  const selectedVersion = versions.find(
    ({ id }) => id === selectedVersionIds[0]
  );

  const { showSuccessNotification, showErrorNotification } =
    useAppNotificationManager();

  const runCancel = async () => {
    if (selectedVersion) {
      try {
        setDialogPhase(null);
        await postCancelVersion({
          organizationId,
          versionId: selectedVersionIds[0],
        });

        showSuccessNotification(
          t.version.cancel.success({
            target,
            versionName: selectedVersion.title,
          })
        );
      } catch (error) {
        showErrorNotification(
          t.version.cancel.error({
            target,
            versionName: selectedVersion.title,
          }),
          error
        );
      } finally {
        setSelectedVersionIds([]);
      }
    }
  };

  return (
    <>
      {renderDialogs({
        confirm: {
          title: t.version.cancel.dialog.title({ target }),
          displayMessage: selectedVersion
            ? t.version.cancel.dialog.message({
                target,
                versionName: selectedVersion.title,
              })
            : t.version.cancel.dialog.messageWithoutName({ target }),
          element: (
            <div
              style={{
                color: "var(--figma-color-status-error)",
                marginTop: 8,
              }}
            >
              {t.version.cancel.dialog.warning()}
            </div>
          ),
          buttonText: t.version.cancel.dialog.button(),
          cancelButtonText: t.version.cancel.dialog.cancelButton(),
          onClick: runCancel,
          onClickCancel: () => {
            setSelectedVersionIds([]);
          },
          isThemeRed: true,
        },
      })}
    </>
  );
};
